<template>
  <Transition name="fade">
    <div class="modal-area" v-if="is_open" style="z-index: 99999;">
      <div class="modal-wrapper vp-wrapper" style="max-width: 680px; width: 95vw;">
        <div class="modal-container vp-container" style="display: flex; flex-direction: column; max-height: 90vh;">

          <!-- Header -->
          <div class="hstack width-100 bb-1-gray p-3 vp-head" style="flex-shrink: 0;">
            <h3 class="text-left fs-r-20 fw-600 fc-black mb-0">{{ title }}</h3>
            <button type="button" class="btn-close ms-auto" @click="close"></button>
          </div>

          <!-- Step indicator -->
          <div class="vp-steps" style="flex-shrink: 0;">
            <div v-for="(label, i) in stepLabels" :key="'s' + i" class="vp-step"
                 :class="{ 'vp-step--active': step === i + 1, 'vp-step--done': step > i + 1 }">
              <span class="vp-step-num">
                <i v-if="step > i + 1" class="bi bi-check"></i>
                <template v-else>{{ i + 1 }}</template>
              </span>
              <span class="vp-step-label">{{ label }}</span>
              <span v-if="i < stepLabels.length - 1" class="vp-step-line"></span>
            </div>
          </div>

          <!-- Body -->
          <div class="vstack p-3 gap-3" style="overflow-y: auto; flex: 1 1 auto;">

            <!-- 1단계: 커넥츠 안내 -->
            <div v-if="step === 1" class="vp-intro">
              <img :src="require('@/assets/img/logo_connects_black.png')" alt="커넥츠" style="height: 26px; align-self: center;">
              <p class="mt-3 mb-0 text-center fw-600" style="font-size: 16px; color: #333;">
                포인핸드 커넥츠는 보호소 동물의 최신 모습을<br>날짜별 생생한 영상과 코멘트로 전합니다.
              </p>
              <div class="vp-intro-list mt-3">
                <div class="vp-intro-item">
                  <i class="bi bi-collection-play"></i>
                  <div>
                    <p class="mb-0 fw-600">공고에 날짜별 영상이 쌓여요</p>
                    <p class="mb-0 vp-intro-sub">날짜별로 등록한 영상이 입력한 설명과 함께 공고에 표시돼요. 입양자는 동물이 지내는 모습을 시간 순서대로 볼 수 있어요.</p>
                  </div>
                </div>
                <div class="vp-intro-item">
                  <i class="bi bi-phone"></i>
                  <div>
                    <p class="mb-0 fw-600">앱 곳곳에 노출돼요</p>
                    <p class="mb-0 vp-intro-sub">커넥츠 등록 후 <b class="vp-accent">추천동물</b>로 등록하면 커넥츠 피드, 홈 화면 등 앱 곳곳에서 예비 입양자들에게 보여져요.</p>
                  </div>
                </div>
                <div class="vp-intro-item">
                  <i class="bi bi-file-play"></i>
                  <div>
                    <p class="mb-0 fw-600">세로 영상을 권장해요</p>
                    <p class="mb-0 vp-intro-sub">세로(9:16)로 촬영한 영상이 가장 보기 좋아요. 파일은 {{ maxSizeMb }}MB 이하로 올려주세요.</p>
                  </div>
                </div>
                <div class="vp-intro-item">
                  <i class="bi bi-calendar-week"></i>
                  <div>
                    <p class="mb-0 fw-600">동물의 최근 모습을 영상으로 업데이트 해주세요.</p>
                    <p class="mb-0 vp-intro-sub">-등록한 영상은 최신순으로 표시됩니다<br>-동일한 날짜에는 1개의 영상만 등록 가능합니다.<br><span class="vp-accent">*동물의 모습을 가리는 효과·스티커 등 과하게 편집된 영상은 지양해주세요.</span></p>
                  </div>
                </div>
              </div>
            </div>

            <!-- 2단계: 영상 첨부 -->
            <template v-else-if="step === 2">
              <div
                v-if="!file"
                class="vp-drop-zone"
                :class="{ 'vp-drop-zone--active': isDragging }"
                @drop.prevent="onDrop"
                @dragover.prevent="isDragging = true"
                @dragleave="isDragging = false"
                @click="$refs.videoInput.click()"
              >
                <input ref="videoInput" type="file" accept="video/*" style="display:none" @change="onFileChange">
                <i class="bi bi-camera-video" style="font-size: 52px; color: #ccc;"></i>
                <p class="mt-2 mb-1" style="color: #999;">클릭하거나 영상을 드래그해서 추가하세요</p>
                <p class="mb-0" style="color: #bbb; font-size: 13px;">{{ maxSizeMb }}MB 이하 · 1개 첨부 가능</p>
              </div>
              <template v-else>
                <div class="vp-preview-wrap">
                  <video :src="previewUrl" controls playsinline preload="metadata"
                         style="max-width:100%; max-height:100%;"></video>
                </div>
                <div class="hstack gap-3 align-items-center">
                  <div class="hstack gap-1 align-items-center">
                    <input type="checkbox" id="vpMuted" v-model="muted" style="width:16px; height:16px; flex-shrink:0;">
                    <label for="vpMuted" class="fw-500" style="font-size:14px; margin-left:5px; color:#333;">음소거 적용</label>
                  </div>
                  <button type="button" class="btn btn-outline-secondary btn-sm ms-auto" @click="reselect">
                    <i class="bi bi-arrow-repeat"></i> 다시 선택
                  </button>
                </div>
              </template>
            </template>

            <!-- 3단계: 날짜 선택 (인라인 달력 + 하단 선택 날짜 표시) -->
            <div v-else-if="step === 3" class="vp-center-step" style="padding: 10px 10px 16px 10px;">
              <p class="mb-3 fw-600" style="font-size: 16px; color: #333;">마지막 등록한 영상 이후 촬영된 최근 영상을 등록해주세요.</p>

              <!-- 선택한 촬영일: 달력 위 배경 박스 + 라벨 + 직접 수정 가능한 텍스트박스(YYYY.MM.DD) -->
              <div class="vp-selected-date mb-3">
                <label class="vp-selected-date-label">촬영 날짜</label>
                <input type="text" class="vp-selected-date-input" v-model="dateInputText"
                       placeholder="YYYY.MM.DD" maxlength="10"
                       @change="onDateInputChange" @keyup.enter="$event.target.blur()">
              </div>

              <div class="vp-cal">
                <div class="vp-cal-head">
                  <button type="button" class="vp-cal-nav" @click="moveMonth(-1)">
                    <i class="bi bi-chevron-left"></i>
                  </button>
                  <!-- 타이틀 버튼: 날짜 뷰 ↔ 월 선택 뷰 토글 -->
                  <button type="button" class="vp-cal-title-btn" @click="monthPickerMode = !monthPickerMode">
                    {{ calTitle }} <i class="bi" :class="monthPickerMode ? 'bi-chevron-up' : 'bi-chevron-down'" style="font-size: 11px;"></i>
                  </button>
                  <button type="button" class="vp-cal-nav" :disabled="!canGoNext" @click="moveMonth(1)">
                    <i class="bi bi-chevron-right"></i>
                  </button>
                </div>

                <!-- 날짜 뷰 (항상 6주 고정 높이) -->
                <div v-if="!monthPickerMode" class="vp-cal-body">
                  <div class="vp-cal-grid vp-cal-week">
                    <span v-for="d in ['일', '월', '화', '수', '목', '금', '토']" :key="d">{{ d }}</span>
                  </div>
                  <div class="vp-cal-grid">
                    <button v-for="(cell, i) in calCells" :key="'c' + i" type="button"
                            class="vp-cal-day"
                            :class="{
                              'vp-cal-day--empty': !cell,
                              'vp-cal-day--selected': cell && cell.date === videoDate,
                              'vp-cal-day--today': cell && cell.date === today,
                              'vp-cal-day--disabled': cell && cell.disabled,
                            }"
                            :disabled="!cell || cell.disabled"
                            @click="cell && selectDate(cell)">
                      {{ cell ? cell.day : '' }}
                    </button>
                  </div>
                </div>

                <!-- 월 선택 뷰 (같은 높이 유지) -->
                <div v-else class="vp-cal-body vp-cal-months">
                  <button v-for="m in 12" :key="'m' + m" type="button"
                          class="vp-cal-month"
                          :class="{
                            'vp-cal-month--selected': calMonth === monthKey(m),
                            'vp-cal-month--disabled': monthKey(m) > thisMonth,
                          }"
                          :disabled="monthKey(m) > thisMonth"
                          @click="selectMonth(m)">
                    {{ m }}월
                  </button>
                </div>
              </div>
            </div>

            <!-- 4단계: 코멘트 등록 -->
            <div v-else-if="step === 4" class="vp-center-step" style="align-items: stretch;">
              <p class="mb-1 fw-600 text-center" style="font-size: 16px; color: #333;">촬영한 날짜에 동물의 모습과 행동에 대한 짧은 코멘트를 남겨주세요.</p>
              <p class="mb-3 vp-intro-sub text-center">커넥츠 화면에서 영상과 함께 보여지는 문구예요. (선택사항)</p>
              <textarea class="vp-comment-input" v-model="comment" rows="4" maxlength="200"
                        placeholder="예) 입소 당시에는 산책을 무서워했는데, 오늘은 호기심 가득하게 여기저기 냄새를 맡으며 즐겁게 걷는 모습을 보여줬어요."></textarea>
              <p class="mb-0 text-right" style="font-size: 12px; color: #bbb;">{{ comment.length }}/200</p>
            </div>

          </div>

          <!-- Footer -->
          <div class="hstack p-3 gap-2 justify-content-end vp-foot" style="border-top: 1px solid #ededed; flex-shrink: 0;">
            <button v-if="step > minStep" type="button" class="btn btn-outline-secondary" @click="step--">이전</button>
            <button v-if="step === minStep" type="button" class="btn btn-outline-secondary" @click="close">취소</button>
            <button v-if="step < 4" type="button" class="btn btn-orange" :disabled="step === 2 && !file" @click="next">다음</button>
            <button v-else type="button" class="btn btn-orange" @click="complete">등록</button>
          </div>

        </div>
      </div>
    </div>
  </Transition>
</template>

<script>
import moment from 'moment';

export default {
  name: 'VideoPickerModal',
  data() {
    return {
      is_open: false,
      from: '',
      step: 1,
      stepLabels: ['커넥츠 안내', '영상 첨부', '날짜 선택', '코멘트 등록'],
      maxSizeMb: 250,
      isDragging: false,
      file: null,
      previewUrl: '',
      muted: false,
      videoDate: '',
      comment: '',
      completed: false,
      existingDates: [],      //이미 등록된 영상들의 촬영일 (동일 날짜 1건 제약 검사용)
      tag: null,              //호출측 식별용(수정 대상 영상 idx 등) — 결과에 그대로 반환
      title: '최근 영상 등록',
      dateOnly: false,        //날짜·코멘트만 수정(파일 첨부 단계 없음 — 등록된 영상 정보 수정용)
      protectedUrl: '',       //호출측이 소유한 미리보기 URL(대기 카드 재수정) — 모달에서 revoke 금지
      minStep: 1,             //시작 단계 (수정 모드는 안내 스킵 → 2)
      minDateExclusive: '',   //이 날짜 '이후'만 선택 가능 (신규=기존 최신 촬영일, 수정=이전 영상 촬영일)
      maxDateExclusive: '',   //이 날짜 '이전'만 선택 가능 (수정=다음 영상 촬영일. 없으면 오늘까지)
      rangeAlertMsg: '이 전에 등록한 영상보다 최신 영상을 등록 부탁드립니다.',
      calMonth: '',           //달력이 표시 중인 월 (YYYY-MM)
      monthPickerMode: false, //타이틀 클릭 시 월 선택 뷰 표시
      dateInputText: '',      //촬영 날짜 텍스트박스 값 (YYYY.MM.DD, 직접 수정 가능)
    };
  },
  computed: {
    today() {
      return moment().format('YYYY-MM-DD');
    },
    thisMonth() {
      return moment().format('YYYY-MM');
    },

    calYear() {
      return this.calMonth ? Number(this.calMonth.substring(0, 4)) : moment().year();
    },
    //타이틀: 날짜 뷰=YYYY년 M월, 월 선택 뷰=YYYY년
    calTitle() {
      if (!this.calMonth) return '';
      return this.monthPickerMode
        ? this.calYear + '년'
        : moment(this.calMonth, 'YYYY-MM').format('YYYY년 M월');
    },
    //다음 이동 가능 여부: 날짜 뷰=다음 달, 월 선택 뷰=다음 해
    canGoNext() {
      if (this.monthPickerMode) return this.calYear < moment().year();
      return this.calMonth < this.thisMonth;
    },
    //표시 중인 월의 셀 목록: 1일 앞 요일만큼 null + 날짜들 + 뒤를 null 로 채워 항상 42칸(6주) 고정.
    //미래/이미 등록된 촬영일은 disabled.
    calCells() {
      if (!this.calMonth) return [];
      const first = moment(this.calMonth + '-01', 'YYYY-MM-DD');
      const cells = [];
      for (let i = 0; i < first.day(); i++) cells.push(null);
      const daysInMonth = first.daysInMonth();
      for (let d = 1; d <= daysInMonth; d++) {
        const date = first.clone().date(d).format('YYYY-MM-DD');
        cells.push({
          day: d,
          date,
          disabled: date > this.today
              || this.existingDates.includes(date)
              || this.isOutOfRange(date),
        });
      }
      while (cells.length < 42) cells.push(null);
      return cells;
    },
  },
  watch: {
    //달력 선택 등으로 videoDate 가 바뀌면 텍스트박스 동기화
    videoDate(v) {
      this.dateInputText = v ? moment(v, 'YYYY-MM-DD').format('YYYY.MM.DD') : '';
    },
  },
  mounted() {
    this.emitter.on('openVideoPickerModal', (params) => {
      this.openModal(params);
    });
  },
  methods: {
    openModal(params = {}) {
      this.from = params.from || '';
      this.maxSizeMb = params.maxSizeMb ?? 250;
      this.tag = params.tag ?? null;
      this.title = params.title || '최근 영상 등록';
      this.minDateExclusive = params.minDateExclusive || '';
      this.maxDateExclusive = params.maxDateExclusive || '';
      this.rangeAlertMsg = params.rangeAlertMsg || '이 전에 등록한 영상보다 최신 영상을 등록 부탁드립니다.';
      this.dateOnly = !!params.dateOnly;
      this.minStep = this.dateOnly ? 3 : (params.skipIntro ? 2 : 1);
      this.step = this.minStep;
      this.isDragging = false;
      //대기 영상 재수정: 기존 파일/미리보기/음소거 프리필 (URL 소유권은 호출측 — revoke 금지)
      this.file = params.initialFile || null;
      this.previewUrl = params.initialPreviewUrl || '';
      this.protectedUrl = params.initialPreviewUrl || '';
      this.muted = !!params.initialMuted;
      this.videoDate = params.initialDate || moment().format('YYYY-MM-DD');
      this.comment = params.initialComment || '';
      this.completed = false;
      this.existingDates = (params.existingDates || []).map(d => String(d).substring(0, 10));
      this.calMonth = moment(this.videoDate, 'YYYY-MM-DD').format('YYYY-MM');
      this.monthPickerMode = false;
      this.dateInputText = this.videoDate ? moment(this.videoDate, 'YYYY-MM-DD').format('YYYY.MM.DD') : '';
      this.is_open = true;
    },
    close() {
      //등록 완료로 닫힌 경우와 호출측 소유 URL(protectedUrl)은 revoke 하지 않음
      if (!this.completed && this.previewUrl && this.previewUrl !== this.protectedUrl) URL.revokeObjectURL(this.previewUrl);
      this.is_open = false;
      this.file = null;
      this.previewUrl = '';
    },
    //‹ › 이동: 날짜 뷰=월 단위, 월 선택 뷰=연 단위 (미래는 이번 달까지로 클램프)
    moveMonth(diff) {
      const unit = this.monthPickerMode ? 'years' : 'months';
      let next = moment(this.calMonth, 'YYYY-MM').add(diff, unit).format('YYYY-MM');
      if (next > this.thisMonth) {
        if (!this.monthPickerMode) return;                                    //날짜 뷰: 미래 달 이동 불가
        if (next.substring(0, 4) > String(moment().year())) return;           //월 선택 뷰: 미래 해 불가
        next = this.thisMonth;                                                //올해 복귀 시 미래 월이면 이번 달로
      }
      this.calMonth = next;
    },
    monthKey(m) {
      return this.calYear + '-' + String(m).padStart(2, '0');
    },
    selectMonth(m) {
      const key = this.monthKey(m);
      if (key > this.thisMonth) return;
      this.calMonth = key;
      this.monthPickerMode = false;
    },
    //선택 가능 범위 밖인지 (min/max 는 경계 '미포함' — 경계일 자체는 이웃 영상 날짜라 선택 불가)
    isOutOfRange(date) {
      if (this.minDateExclusive && date <= this.minDateExclusive) return true;
      if (this.maxDateExclusive && date >= this.maxDateExclusive) return true;
      return false;
    },
    selectDate(cell) {
      if (!cell || cell.disabled) return;
      this.videoDate = cell.date;
    },
    //텍스트박스 직접 입력: YYYY.MM.DD(구분자 . - / 허용) 파싱 → 유효하면 선택 반영, 아니면 원래 값으로 복원
    onDateInputChange() {
      const revert = () => {
        this.dateInputText = this.videoDate ? moment(this.videoDate, 'YYYY-MM-DD').format('YYYY.MM.DD') : '';
      };
      const t = (this.dateInputText || '').trim().replace(/[./]/g, '-');
      const m = moment(t, 'YYYY-M-D', true);
      if (!m.isValid()) { revert(); return; }
      const date = m.format('YYYY-MM-DD');
      if (date > this.today) {
        alert('미래 날짜는 선택할 수 없어요.');
        revert();
        return;
      }
      if (this.existingDates.includes(date)) {
        alert('선택한 촬영일에 이미 등록된 영상이 있습니다.\n동일한 날짜에는 1개의 영상만 등록할 수 있어요.');
        revert();
        return;
      }
      if (this.isOutOfRange(date)) {
        alert(this.rangeAlertMsg);
        revert();
        return;
      }
      this.videoDate = date;
      this.calMonth = date.substring(0, 7);   //달력도 해당 월로 이동
      this.monthPickerMode = false;
    },
    next() {
      if (this.step === 2 && !this.file) return;
      if (this.step === 3) {
        if (!this.videoDate) {
          alert('촬영일을 선택해주세요.');
          return;
        }
        //동일 날짜 1건 제약: 이미 그 촬영일로 등록된 영상이 있으면 진행 불가
        if (this.existingDates.includes(this.videoDate.substring(0, 10))) {
          alert('선택한 촬영일에 이미 등록된 영상이 있습니다.\n동일한 날짜에는 1개의 영상만 등록할 수 있어요.');
          return;
        }
        //선택 가능 범위 검사 (안전망 — 달력에서는 범위 밖이 비활성)
        if (this.isOutOfRange(this.videoDate.substring(0, 10))) {
          alert(this.rangeAlertMsg);
          return;
        }
      }
      this.step++;
    },
    onDrop(event) {
      this.isDragging = false;
      const file = Array.from(event.dataTransfer.files).find(f => f.type.startsWith('video/'));
      if (file) this.setFile(file);
    },
    onFileChange(event) {
      const file = event.target.files && event.target.files[0];
      event.target.value = '';
      if (file) this.setFile(file);
    },
    setFile(file) {
      if (file.size > this.maxSizeMb * 1024 * 1024) {
        alert('영상 파일이 너무 큽니다. ' + this.maxSizeMb + 'MB 이하의 영상으로 올려주세요.');
        return;
      }
      if (this.previewUrl && this.previewUrl !== this.protectedUrl) URL.revokeObjectURL(this.previewUrl);
      this.file = file;
      this.previewUrl = URL.createObjectURL(file);
    },
    reselect() {
      if (this.previewUrl && this.previewUrl !== this.protectedUrl) URL.revokeObjectURL(this.previewUrl);
      this.file = null;
      this.previewUrl = '';
      this.muted = false;
    },
    complete() {
      if (!this.file && !this.dateOnly) return;
      this.completed = true;
      this.emitter.emit('videoPickerResult', {
        from: this.from,
        tag: this.tag,
        file: this.file,
        previewUrl: this.previewUrl,
        videoDate: this.videoDate,
        comment: this.comment.trim(),
        muted: this.muted,
      });
      this.close();
    },
  },
};
</script>

<style scoped>
@import '@/assets/css/common/common.css';
@import '@/assets/css/style/style.css';
/* 안내 문장이 단어 중간에서 줄바꿈되지 않게 (예: '표시돼/요') */
.vp-intro-sub { word-break: keep-all; }
/* 안내 강조 (추천동물·편집 주의 문구) */
.vp-intro-sub .vp-accent, .vp-accent { color: #f6951c !important; }
b.vp-accent { font-weight: 700; }

/* ── 단계 표시 ── */
.vp-steps {
  display: flex;
  justify-content: center;
  gap: 6px;
  padding: 14px 12px 4px 12px;
}
.vp-step {
  display: flex;
  align-items: center;
  gap: 6px;
}
.vp-step-num {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #eee;
  color: #999;
  font-size: 12px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.vp-step-label {
  font-size: 13px;
  color: #999;
  font-weight: 500;
  white-space: nowrap;
}
.vp-step--active .vp-step-num {
  background: #f6951c;
  color: #fff;
}
.vp-step--active .vp-step-label {
  color: #f6951c;
  font-weight: 700;
}
.vp-step--done .vp-step-num {
  background: #ffe3c2;
  color: #f6951c;
}
.vp-step--done .vp-step-label {
  color: #555;
}
.vp-step-line {
  width: 18px;
  height: 1px;
  background: #ddd;
  margin: 0 2px;
}

/* ── 1단계: 안내 ── */
.vp-intro {
  display: flex;
  flex-direction: column;
  padding: 16px 8px;
}
.vp-intro-list {
  display: flex;
  flex-direction: column;
  gap: 14px;
  max-width: 440px;
  margin: 0 auto;
  width: 100%;
}
.vp-intro-item {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  background: #faf9f7;
  border-radius: 10px;
  padding: 12px 14px;
  text-align: left;
}
.vp-intro-item i {
  font-size: 22px;
  color: #f6951c;
  flex-shrink: 0;
  margin-top: 2px;
}
.vp-intro-item .fw-600 {
  font-size: 14px;
  color: #333;
}
.vp-intro-sub {
  font-size: 13px;
  color: #999;
  line-height: 1.5;
}

/* ── 2단계: 첨부 ── */
.vp-drop-zone {
  border: 2px dashed #ddd;
  border-radius: 12px;
  padding: 60px 20px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  transition: border-color 0.2s, background 0.2s;
  user-select: none;
}
.vp-drop-zone:hover,
.vp-drop-zone--active {
  border-color: #f6951c;
  background: #fff8f0;
}
.vp-preview-wrap {
  width: 100%;
  height: 360px;
  background: #1a1a1a;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* ── 3·4단계 공통 ── */
.vp-center-step {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 30px 10px;
}
/* ── 자체 인라인 달력 (간결한 디자인) ── */
.vp-cal {
  width: 100%;
  max-width: 320px;
}
.vp-cal-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}
.vp-cal-title-btn {
  border: none;
  background: none;
  font-size: 15px;
  font-weight: 700;
  color: #333;
  cursor: pointer;
  padding: 5px 12px;
  border-radius: 8px;
  transition: background 0.15s;
}
.vp-cal-title-btn:hover {
  background: #f5f5f5;
}

/* 날짜/월 선택 뷰 공통 몸통 — 높이 고정(항상 6주 분량)으로 월 이동 시 흔들림 없음 */
.vp-cal-body {
  height: 254px;
}
.vp-cal-months {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  align-content: center;
  padding: 8px 4px;
}
.vp-cal-month {
  border: none;
  background: #faf9f7;
  border-radius: 8px;
  padding: 12px 0;
  font-size: 14px;
  color: #333;
  cursor: pointer;
  transition: background 0.15s;
}
.vp-cal-month:hover:not(:disabled) {
  background: #fff3e4;
}
.vp-cal-month--selected,
.vp-cal-month--selected:hover:not(:disabled) {
  background: #f6951c;
  color: #fff;
  font-weight: 700;
}
.vp-cal-month--disabled {
  color: #ddd;
  cursor: default;
  background: #fcfcfc;
}
.vp-cal-nav {
  border: none;
  background: none;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  color: #888;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;
  padding: 0;
}
.vp-cal-nav:hover:not(:disabled) {
  background: #f5f5f5;
  color: #333;
}
.vp-cal-nav:disabled {
  color: #ddd;
  cursor: default;
}
.vp-cal-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  row-gap: 2px;
  justify-items: center;
}
.vp-cal-week span {
  font-size: 12px;
  color: #bbb;
  font-weight: 600;
  padding-bottom: 6px;
}
.vp-cal-day {
  border: none;
  background: none;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  font-size: 14px;
  color: #333;
  cursor: pointer;
  padding: 0;
  line-height: 1;
  transition: background 0.15s;
}
.vp-cal-day:hover:not(:disabled) {
  background: #fff3e4;
}
.vp-cal-day--empty {
  visibility: hidden;
}
.vp-cal-day--today {
  font-weight: 700;
  color: #f6951c;
}
.vp-cal-day--selected,
.vp-cal-day--selected:hover:not(:disabled) {
  background: #f6951c;
  color: #fff;
  font-weight: 700;
}
.vp-cal-day--disabled {
  color: #ddd;
  cursor: default;
}

/* 달력 위 선택 날짜: 배경 박스 + 중앙정렬 라벨 + 직접 수정 가능한 텍스트박스 */
.vp-selected-date {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 7px;
  width: 100%;
  max-width: 320px;
  background: #faf9f7;
  border-radius: 10px;
  padding: 14px 20px;
}
.vp-selected-date-label {
  font-size: 14px;
  font-weight: 700;
  color: #111;
}
.vp-selected-date-input {
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 9px 12px;
  font-size: 15px;
  font-weight: 600;
  color: #333;
  text-align: center;
  background: #fff;
  outline: none;
  width: 160px;
}
.vp-selected-date-input:focus {
  border-color: #f6951c;
}
.vp-comment-input {
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 14px;
  color: #333;
  outline: none;
  resize: vertical;
  width: 100%;
  max-width: 440px;
  align-self: center;
}
.vp-comment-input:focus {
  border-color: #f6951c;
}

/* ── 모바일: 아래 시트 가로 꽉 차게, 단계 표시는 지금 단계 이름만, 버튼 크게 ── */
@media (max-width: 768px) {
  .vp-wrapper { width: 100% !important; max-width: 100% !important; margin: 0 !important; }
  .vp-container { max-height: 92dvh !important; }
  .vp-head .btn-close { padding: 10px; }
  .vp-steps { gap: 4px; padding: 12px 8px 4px; }
  .vp-step { gap: 4px; }
  .vp-step:not(.vp-step--active) .vp-step-label { display: none; }
  .vp-step-line { width: 14px; }
  .vp-intro { padding: 8px 0; }
  .vp-intro-item { padding: 12px; }
  .vp-drop-zone { padding: 36px 16px; min-height: 180px; }
  .vp-preview-wrap { height: min(52vh, 360px); }
  .vp-cal-day { width: 40px; height: 40px; }
  .vp-cal-nav { width: 40px; height: 40px; }
  .vp-cal-body { height: auto; min-height: 254px; }
  .vp-foot { padding-bottom: calc(1rem + env(safe-area-inset-bottom)) !important; }
  .vp-foot .btn { flex: 1; min-height: 44px; }
}
</style>
